import React, { Suspense, lazy, useState, useCallback } from 'react';
import './App.css';
import { Router, Routes, Route, useLocation } from './utils/simpleRouter';
import { debugLog } from './utils/logger';

const Header = lazy(() => import('./components/Header'));
const WordExplorer = lazy(() => import('./components/WordExplorer'));
const BaybayinConverter = lazy(() => import('./components/BaybayinConverter'));
const BaybayinSearch = lazy(() => import('./components/BaybayinSearch'));
const BaybayinStatistics = lazy(() => import('./components/BaybayinStatistics'));

const routeFallback = (
  <div className="route-loading" role="status">
    Loading...
  </div>
);

// Simple error boundary component
class ErrorBoundary extends React.Component<{children: React.ReactNode}, {hasError: boolean, error: Error | null}> {
  constructor(props: {children: React.ReactNode}) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Error caught by App ErrorBoundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ 
          padding: '20px', 
          color: '#721c24',
          backgroundColor: '#f8d7da',
          borderRadius: '5px',
          margin: '20px',
          fontFamily: 'system-ui, -apple-system, sans-serif'
        }}>
          <h2>Application Error</h2>
          <p>{this.state.error?.message || "An unknown error occurred"}</p>
          <button 
            onClick={() => window.location.reload()} 
            style={{
              marginTop: '10px',
              padding: '8px 16px',
              backgroundColor: '#721c24',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Reload Application
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

const AppContent: React.FC = () => {
  const location = useLocation();
  const showGlobalHeader = location.pathname !== '/';
  const [apiConnected, setApiConnected] = useState<boolean | null>(null);

  const handleTestApiConnection = useCallback(async () => {
    try {
      const { testApiConnection } = await import('./api/wordApi');
      const result = await testApiConnection();
      setApiConnected(result);
    } catch (err) {
      console.error("Error testing API connection:", err);
      setApiConnected(false);
    }
  }, []);

  const handleResetCircuitBreaker = useCallback(async () => {
    try {
      const { resetCircuitBreaker } = await import('./api/wordApi');
      resetCircuitBreaker();
      debugLog("Circuit breaker reset successful");
      await handleTestApiConnection();
    } catch (err) {
      console.error("Error resetting circuit breaker:", err);
    }
  }, [handleTestApiConnection]);

  // Test API connection on initial load
  React.useEffect(() => {
    if (showGlobalHeader) {
      handleTestApiConnection();
    }
  }, [handleTestApiConnection, showGlobalHeader]);

  return (
    <div className="app">
      {showGlobalHeader && (
        <Suspense fallback={null}>
          <Header
            title="Fil-Relex: Filipino Root Explorer"
            onTestApiConnection={handleTestApiConnection}
            onResetCircuitBreaker={handleResetCircuitBreaker}
            apiConnected={apiConnected}
          />
        </Suspense>
      )}
      <Suspense fallback={routeFallback}>
        <Routes>
          <Route path="/" element={<WordExplorer />} />
          <Route path="/baybayin/converter" element={<BaybayinConverter />} />
          <Route path="/baybayin/search" element={<BaybayinSearch />} />
          <Route path="/baybayin/statistics" element={<BaybayinStatistics />} />
        </Routes>
      </Suspense>
    </div>
  );
};

// Create a simple application component
const App: React.FC = () => {

  return (
    <ErrorBoundary>
      <Router>
        <AppContent />
      </Router>
    </ErrorBoundary>
  );
};

export default App;
